// src/components/customer-detail/recent-activity-panel.tsx

import { useQueryClient } from '@tanstack/react-query';
import {
  Banknote,
  Calendar,
  CheckCircle2,
  FileText,
  FileWarning,
  Loader2,
  Mail,
  MessageSquare,
  Phone,
} from 'lucide-react';
import type { ReactNode } from 'react';
import { useState } from 'react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { useCustomerRecentActivities } from '@/hooks/useCustomerRecentActivitiesGraphQL';
import { useAddCustomerNote } from '@/hooks/useCustomerNotes';
import { ACTIVITY_TYPE_TO_TYPE } from '@/lib/activity-type-maps';
import { mockUsers } from '@/lib/mock-users';
import { formatDateTime } from '@/lib/utils';

interface RecentActivityPanelProps {
  customerId: string;
}

const MAX_ACTIVITIES = 4;

function getActivityIcon(type: string): ReactNode {
  switch (type) {
    case 'call':
      return <Phone className="h-4 w-4" />;
    case 'email':
      return <Mail className="h-4 w-4" />;
    case 'payment':
      return <Banknote className="h-4 w-4" />;
    case 'promise':
      return <CheckCircle2 className="h-4 w-4" />;
    case 'note':
      return <MessageSquare className="h-4 w-4" />;
    case 'dispute':
      return <FileWarning className="h-4 w-4" />;
    case 'letter':
      return <FileText className="h-4 w-4" />;
    case 'meeting':
      return <Calendar className="h-4 w-4" />;
    default:
      return <MessageSquare className="h-4 w-4" />;
  }
}

function getActivityColor(type: string): string {
  switch (type) {
    case 'call':
      return 'bg-blue-100 text-blue-700';
    case 'email':
      return 'bg-purple-100 text-purple-700';
    case 'payment':
      return 'bg-green-100 text-green-700';
    case 'promise':
      return 'bg-cyan-100 text-cyan-700';
    case 'note':
      return 'bg-gray-100 text-gray-700';
    case 'dispute':
      return 'bg-red-100 text-red-700';
    case 'letter':
      return 'bg-orange-100 text-orange-700';
    case 'meeting':
      return 'bg-indigo-100 text-indigo-700';
    default:
      return 'bg-gray-100 text-gray-700';
  }
}

export function RecentActivityPanel({ customerId }: RecentActivityPanelProps) {
  const [note, setNote] = useState('');
  const customerIdNum = Number.parseInt(customerId, 10);
  const { activities, isLoading } = useCustomerRecentActivities(
    Number.isNaN(customerIdNum) ? undefined : customerIdNum,
  );

  const addNoteMutation = useAddCustomerNote();
  const queryClient = useQueryClient();
  const currentUser = mockUsers[0];

  const handleAddNote = async () => {
    if (!note.trim()) return;

    try {
      await addNoteMutation.mutateAsync({
        customerId,
        content: note.trim(),
        userId: currentUser.id,
        userName: `${currentUser.firstName} ${currentUser.lastName}`,
      });

      setNote('');
      toast.success('Note added successfully');
      queryClient.invalidateQueries({ queryKey: ['customer-notes', customerId] });
      queryClient.invalidateQueries({ queryKey: ['customer-audit-logs', customerId] });
      queryClient.invalidateQueries({ queryKey: ['customer-recent-activities', customerIdNum] });
    } catch (error) {
      toast.error('Failed to add note');
      console.error('Error adding note:', error);
    }
  };

  const formatRelativeDate = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();

    const startOfToday = new Date(now);
    startOfToday.setHours(0, 0, 0, 0);

    const startOfActivityDay = new Date(date);
    startOfActivityDay.setHours(0, 0, 0, 0);

    const diffDays = Math.round((startOfToday.getTime() - startOfActivityDay.getTime()) / (1000 * 60 * 60 * 24));

    if (diffDays <= 0) return 'Today';
    if (diffDays === 1) return 'Yesterday';
    if (diffDays < 7) return `${diffDays} days ago`;

    return formatDateTime(dateString);
  };

  const latestActivities = activities.slice(0, MAX_ACTIVITIES);

  const renderActivitiesContent = () => {
    if (isLoading) {
      return (
        <div className="flex items-center justify-center py-4">
          <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
        </div>
      );
    }

    if (latestActivities.length === 0) {
      return <p className="text-sm text-muted-foreground text-center py-4">No recent activity recorded</p>;
    }

    return latestActivities.map((activity, index) => {
      const uiType = ACTIVITY_TYPE_TO_TYPE[activity.activityType] ?? activity.activityType;
      const isLast = index === latestActivities.length - 1;

      return (
        <div key={activity.id} className="relative">
          {!isLast && <div className="absolute left-[15px] top-8 bottom-0 w-px bg-border" />}

          <div className="flex gap-3">
            <div className="flex-shrink-0 mt-0.5">
              <div className={`h-8 w-8 rounded-full flex items-center justify-center ${getActivityColor(uiType)}`}>
                {getActivityIcon(uiType)}
              </div>
            </div>

            <div className={`flex-1 space-y-1 ${isLast ? 'pb-0' : 'pb-4'}`}>
              <div className="flex items-start justify-between gap-2">
                <div className="flex-1">
                  <p className="text-sm font-medium leading-tight">{activity.title}</p>
                  {activity.description && (
                    <p className="text-sm text-muted-foreground leading-tight whitespace-pre-line mt-0.5">
                      {activity.description}
                    </p>
                  )}
                </div>
                <span className="text-xs text-muted-foreground whitespace-nowrap">
                  {formatRelativeDate(activity.createdAt)}
                </span>
              </div>
              {activity.actorName && <p className="text-xs text-muted-foreground">{activity.actorName}</p>}
            </div>
          </div>
        </div>
      );
    });
  };

  return (
    <Card className="h-full flex flex-col">
      <CardHeader className="pb-3">
        <div className="flex items-center gap-3">
          <CardTitle className="flex items-center gap-2 flex-shrink-0">
            <MessageSquare className="h-5 w-5" />
            Recent Activity
          </CardTitle>
          <div className="flex-1 flex items-center gap-2">
            <Input
              placeholder="Add a quick note..."
              value={note}
              onChange={(e) => setNote(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  handleAddNote();
                }
              }}
              className="h-9"
            />
            <Button
              size="sm"
              onClick={handleAddNote}
              disabled={!note.trim() || addNoteMutation.isPending}
              className="flex-shrink-0"
            >
              Add Note
            </Button>
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-4 flex-1">
        {/* Activity Timeline */}
        <div className="space-y-4 pt-2">{renderActivitiesContent()}</div>
      </CardContent>
    </Card>
  );
}
